import { OnChange } from '@/features/form/types'
import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import {
    FormCustomerKeys,
    FormKeys,
} from '@/features/pos/components/OrderFinalization/form/constants'
import {
    AutomaticContainerRemovalsInput,
    CustomerAddressInput,
    CustomerInput,
    DeliveryTimeInput,
    FormContext,
    FormInput,
    FormOutput,
    PhoneFormInput,
    PhoneFormOutput,
} from '@/features/pos/components/OrderFinalization/form/types'
import { getDefaultValues } from '@/features/pos/components/OrderFinalization/form/utils/getDefaultValues'
import { dynamicResolver } from '@/features/pos/components/OrderFinalization/form/utils/resolver'
import { PosMode } from '@/features/pos/types'
import { useCallback, useEffect } from 'react'
import {
    UseFormReturn,
    useController,
    useFormContext,
    useForm as useRHFForm,
    useWatch,
} from 'react-hook-form'

const emptyAutomaticContainerRemovals: AutomaticContainerRemovalsInput = []

export const useForm = (
    posMode: PosMode,
    defaultDeliveryTypeCategory?: DeliveryTypeCategory
): UseFormReturn<FormInput, FormContext, FormOutput> => {
    const methods = useRHFForm<FormInput, FormContext, FormOutput>({
        defaultValues:
            defaultDeliveryTypeCategory && getDefaultValues(posMode, defaultDeliveryTypeCategory),
        resolver: dynamicResolver,
        context: { posMode },
    })

    useEffect(() => {
        if (!defaultDeliveryTypeCategory) return

        methods.resetField(FormKeys.DeliveryTypeCategory, {
            defaultValue: defaultDeliveryTypeCategory,
        }) // needed to make the "reset" method work
        methods.reset(getDefaultValues(posMode, defaultDeliveryTypeCategory))
    }, [posMode, defaultDeliveryTypeCategory, methods])

    return methods
}

export const useFormDeliveryTypeCategory = (): [DeliveryTypeCategory, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.DeliveryTypeCategory>({
        name: FormKeys.DeliveryTypeCategory,
    })

    return [value, onChange]
}

export const useFormDeliveryTypeCategoryValue = (): DeliveryTypeCategory => {
    return useWatch<FormInput, FormKeys.DeliveryTypeCategory>({
        name: FormKeys.DeliveryTypeCategory,
    })
}

export const useFormCustomerField = (): CustomerInput => {
    const {
        field: { value },
    } = useController<FormInput, FormKeys.Customer>({ name: FormKeys.Customer })

    return value
}

export const useFormSetCustomerField = (): ((customer: CustomerInput) => void) => {
    const { setValue } = useFormContext<FormInput>()

    return useCallback(
        (customer: CustomerInput) => setValue(FormKeys.Customer, customer),
        [setValue]
    )
}

export const useFormTriggerCustomerField = (): (() => void) => {
    const { trigger } = useFormContext<FormInput>()

    return useCallback(() => trigger(FormKeys.Customer), [trigger])
}

export const useFormCustomerAddressValue = (): CustomerAddressInput => {
    return useWatch<FormInput, `${FormKeys.Customer}.${FormCustomerKeys.Address}`>({
        name: `${FormKeys.Customer}.${FormCustomerKeys.Address}`,
    })
}

export const useFormDeliveryTimeField = (): DeliveryTimeInput => {
    const {
        field: { value },
    } = useController<FormInput, FormKeys.DeliveryTime>({ name: FormKeys.DeliveryTime })

    return value
}

export const useFormNoteField = (): [string | null, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.Note>({ name: FormKeys.Note })

    return [value, onChange]
}

export const useFormAutomaticContainerRemovalsField = (): [
    AutomaticContainerRemovalsInput,
    OnChange,
] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.AutomaticContainerRemovals>({
        name: FormKeys.AutomaticContainerRemovals,
    })

    return [value ?? emptyAutomaticContainerRemovals, onChange]
}

export const useFormAutomaticContainerRemovalsValue = (): AutomaticContainerRemovalsInput => {
    return (
        useWatch<FormInput, FormKeys.AutomaticContainerRemovals>({
            name: FormKeys.AutomaticContainerRemovals,
        }) ?? emptyAutomaticContainerRemovals
    )
}

export const useFormSetDeliveryTimeField = (): ((deliveryTime: DeliveryTimeInput) => void) => {
    const { setValue } = useFormContext<FormInput>()

    return useCallback(
        (deliveryTime: DeliveryTimeInput) => setValue(FormKeys.DeliveryTime, deliveryTime),
        [setValue]
    )
}

export const useFormTriggerDeliveryTimeField = (): (() => void) => {
    const { trigger } = useFormContext<FormInput>()

    return useCallback(() => trigger(FormKeys.DeliveryTime), [trigger])
}

export const useFormIsDefaultValuesInitialized = (): boolean => {
    const { formState } = useFormContext<FormInput>()

    return !!formState.defaultValues
}

export const useFormGetValues = () => {
    const { getValues } = useFormContext<FormInput>()

    return getValues
}

export const usePhoneFormAsyncSubmit = <Arg>(
    onFormSubmit: (form: PhoneFormOutput, arg: Arg) => Promise<void>
): ((arg: Arg) => Promise<void>) => {
    const { handleSubmit } = useFormContext<PhoneFormInput, FormContext, PhoneFormOutput>()

    return (arg: Arg) =>
        new Promise<void>((resolve, reject) =>
            handleSubmit(async (data) => {
                await onFormSubmit(data, arg)

                resolve()
            }, reject)()
        )
}

export const useResetForm = (): (() => void) => {
    const { reset } = useFormContext<PhoneFormInput, FormContext, PhoneFormOutput>()

    return reset
}
